"use client";

import * as React from "react";
import Link from "next/link";
import { ArrowUpRight, ClipboardList, Newspaper } from "lucide-react";
import Section from "./Section";
import { REVIEW_LINKS, ReviewBanner } from "@/components/past-exam/reviewLinks";

const PREPARATION_LINKS = [
  { href: "/exam-registration/jeongcheogi-application", title: "원서 접수 안내", description: "접수 일정과 준비물 확인" },
  { href: "/exam-registration/jeongcheogi-eligibility", title: "응시 자격 확인", description: "응시 조건과 제출 서류 확인" },
  { href: "/exam-registration/jcg-test-center", title: "시험장 찾기", description: "지역별 시험장과 위치 확인" },
  { href: "/past-exam", title: "기출문제 모아보기", description: "회차별 복원 문제로 학습하기" },
];

/** 후기·시험 안내의 공개 바로가기. 현재 페이지로 돌아오는 링크는 제외한다. */
export function ExamPreparationCard({ pathname }: { pathname: string }) {
  const [open, setOpen] = React.useState(true);
  const path = pathname.replace(/\/+$/, "");
  const review = /^\/past-exam\/(\d{4})\/\d{4}-(\d+)-review$/.exec(path);
  const links = PREPARATION_LINKS.filter((link) =>
    link.href !== path && (!review || !link.href.endsWith("jeongcheogi-eligibility")),
  );

  return (
    <Section
      icon={ClipboardList}
      label="시험 준비 바로가기"
      amber
      open={open}
      onToggle={() => setOpen((value) => !value)}
    >
      <nav aria-label="시험 준비 바로가기" className="pt-2">
        {review && (
          <Link
            href={path.replace(/-review$/, "")}
            className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-950/70"
          >
            <span className="min-w-0">
              <span className="block text-sm font-bold">이 회차 기출 풀기</span>
              <span className="mt-1 block text-xs">{review[1]}년 {review[2]}회 복원 문제</span>
            </span>
            <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
        )}
        <div className="divide-y divide-border">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="flex items-center justify-between gap-2 rounded-md px-1 py-3 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-inset"
            >
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-foreground">{link.title}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{link.description}</span>
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            </Link>
          ))}
        </div>
      </nav>
    </Section>
  );
}

/**
 * 합격률 페이지(jeongcheogi-pass-rate) 우측 사이드바 상단 "회차별 리뷰" 카드.
 *
 * 데스크탑 셸은 좌측 사이드바에 실기 합격률 추이 미니 차트(PassRateMiniChart)를 두고,
 * 이 카드를 우측 사이드바 맨 위에 둬 최신 회차 실기 후기(REVIEW_LINKS)로 바로 이동하게 한다.
 * 좁은 우측 사이드바(약 320px)에 맞춰 ReviewBanner 를 세로로 쌓는다.
 *
 * RoundStatsCard 처럼 자체 펼침 상태를 관리해 셸 상태(localStorage 키·토글 콜백)에 침투하지 않는다.
 */
export default function PassRateReviewCard() {
  const [open, setOpen] = React.useState(true);

  return (
    <Section
      icon={Newspaper}
      label="회차별 리뷰"
      amber
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <div className="flex flex-col gap-2 pt-1">
        {REVIEW_LINKS.map((review) => (
          <ReviewBanner key={review.href} review={review} />
        ))}
      </div>
    </Section>
  );
}
